import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import './SetupWizard.css';

function SetupWizard({ user, hierarchy, onComplete, onClose }) {
  const [step, setStep] = useState(0); // 0 = welcome, 1 = languages, 2 = genres, 3 = subgenres, 4 = complete
  const [loading, setLoading] = useState(false);
  
  // Track what's INCLUDED (unchecked = excluded)
  const [selectedLanguages, setSelectedLanguages] = useState(new Set());
  const [selectedGenres, setSelectedGenres] = useState(new Set());
  const [selectedSubgenres, setSelectedSubgenres] = useState(new Set());
  
  // Available options from hierarchy
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableGenres, setAvailableGenres] = useState([]);
  const [availableSubgenres, setAvailableSubgenres] = useState([]);

  // Extract available options from hierarchy
  useEffect(() => {
    if (!hierarchy) return;

    console.log('SetupWizard: Processing hierarchy', hierarchy);

    // Hierarchy structure:
    // - languages: string[]
    // - genres: { [language]: string[] }
    // - subgenres: { [language|genre]: string[] }
    
    const languages = hierarchy.languages || [];
    
    // Build genres array
    const genresArray = [];
    if (hierarchy.genres) {
      Object.keys(hierarchy.genres).forEach(language => {
        const genreList = hierarchy.genres[language] || [];
        genreList.forEach(genre => {
          genresArray.push({ language, genre, key: `${language}|${genre}` });
        });
      });
    }
    
    // Build subgenres array
    const subgenresArray = [];
    if (hierarchy.subgenres) {
      Object.keys(hierarchy.subgenres).forEach(genreKey => {
        const [language, genre] = genreKey.split('|');
        const subgenreList = hierarchy.subgenres[genreKey] || [];
        subgenreList.forEach(subgenre => {
          subgenresArray.push({ 
            language, 
            genre, 
            subgenre, 
            key: `${language}|${genre}|${subgenre}` 
          });
        });
      });
    }

    console.log('SetupWizard: Found', languages.length, 'languages,', genresArray.length, 'genres,', subgenresArray.length, 'subgenres');

    setAvailableLanguages(languages.sort());
    setAvailableGenres(genresArray.sort((a, b) => a.key.localeCompare(b.key)));
    setAvailableSubgenres(subgenresArray.sort((a, b) => a.key.localeCompare(b.key)));

    // Default: all selected
    setSelectedLanguages(new Set(languages));
    setSelectedGenres(new Set(genresArray.map(g => g.key)));
    setSelectedSubgenres(new Set(subgenresArray.map(s => s.key)));
  }, [hierarchy]);

  // Save preferences (Firebase with localStorage fallback)
  const savePreferences = async (prefs) => {
    // Always save to localStorage as backup
    localStorage.setItem(`jookbox_prefs_${user.uid}`, JSON.stringify(prefs));
    
    try {
      // Try to save to Firebase
      await setDoc(doc(db, 'userPreferences', user.uid), prefs, { merge: true });
      console.log('✅ Preferences saved to Firebase');
      return true;
    } catch (error) {
      console.error('Firebase save error:', error);
      console.log('⚠️ Using localStorage fallback');
      // Still return true since we saved to localStorage
      return true;
    }
  };

  // Handle skip - accept all (always closes wizard)
  const handleSkip = async () => {
    setLoading(true);
    
    const prefs = {
      excludedLanguages: [],
      excludedGenres: [],
      excludedSubgenres: [],
      excludedArtists: [],
      excludedAlbums: [],
      setupCompleted: true,
      updatedAt: new Date().toISOString()
    };
    
    await savePreferences(prefs);
    // Always close wizard, even if save had issues
    onComplete();
    setLoading(false);
  };

  // Handle complete - save exclusions (always closes wizard)
  const handleComplete = async () => {
    setLoading(true);
    
    // Calculate exclusions (what's NOT selected)
    const excludedLanguages = availableLanguages.filter(l => !selectedLanguages.has(l));
    const excludedGenres = availableGenres
      .filter(g => !selectedGenres.has(g.key))
      .map(g => g.key);
    const excludedSubgenres = availableSubgenres
      .filter(s => !selectedSubgenres.has(s.key))
      .map(s => s.key);

    const prefs = {
      excludedLanguages,
      excludedGenres,
      excludedSubgenres,
      excludedArtists: [],
      excludedAlbums: [],
      setupCompleted: true,
      updatedAt: new Date().toISOString()
    };
    
    await savePreferences(prefs);
    // Always close wizard, even if save had issues
    onComplete();
    setLoading(false);
  };

  // Toggle language selection
  const toggleLanguage = (language) => {
    const newSelected = new Set(selectedLanguages);
    if (newSelected.has(language)) {
      newSelected.delete(language);
      // Also deselect all genres and subgenres for this language
      const newGenres = new Set(selectedGenres);
      const newSubgenres = new Set(selectedSubgenres);
      availableGenres.forEach(g => {
        if (g.language === language) {
          newGenres.delete(g.key);
        }
      });
      availableSubgenres.forEach(s => {
        if (s.language === language) {
          newSubgenres.delete(s.key);
        }
      });
      setSelectedGenres(newGenres);
      setSelectedSubgenres(newSubgenres);
    } else {
      newSelected.add(language);
      // Also select all genres and subgenres for this language
      const newGenres = new Set(selectedGenres);
      const newSubgenres = new Set(selectedSubgenres);
      availableGenres.forEach(g => {
        if (g.language === language) {
          newGenres.add(g.key);
        }
      });
      availableSubgenres.forEach(s => {
        if (s.language === language) {
          newSubgenres.add(s.key);
        }
      });
      setSelectedGenres(newGenres);
      setSelectedSubgenres(newSubgenres);
    }
    setSelectedLanguages(newSelected);
  };

  // Toggle genre selection
  const toggleGenre = (genreKey) => {
    const newSelected = new Set(selectedGenres);
    const [language, genre] = genreKey.split('|');
    
    if (newSelected.has(genreKey)) {
      newSelected.delete(genreKey);
      // Also deselect all subgenres for this genre
      const newSubgenres = new Set(selectedSubgenres);
      availableSubgenres.forEach(s => {
        if (s.language === language && s.genre === genre) {
          newSubgenres.delete(s.key);
        }
      });
      setSelectedSubgenres(newSubgenres);
    } else {
      newSelected.add(genreKey);
      // Also select all subgenres for this genre
      const newSubgenres = new Set(selectedSubgenres);
      availableSubgenres.forEach(s => {
        if (s.language === language && s.genre === genre) {
          newSubgenres.add(s.key);
        }
      });
      setSelectedSubgenres(newSubgenres);
    }
    setSelectedGenres(newSelected);
  };

  // Toggle subgenre selection
  const toggleSubgenre = (subgenreKey) => {
    const newSelected = new Set(selectedSubgenres);
    if (newSelected.has(subgenreKey)) {
      newSelected.delete(subgenreKey);
    } else {
      newSelected.add(subgenreKey);
    }
    setSelectedSubgenres(newSelected);
  };

  // Select/Deselect all for current step
  const selectAll = () => {
    if (step === 1) {
      setSelectedLanguages(new Set(availableLanguages));
      setSelectedGenres(new Set(availableGenres.map(g => g.key)));
      setSelectedSubgenres(new Set(availableSubgenres.map(s => s.key)));
    } else if (step === 2) {
      // Select all genres for selected languages
      const newGenres = new Set();
      const newSubgenres = new Set();
      availableGenres.forEach(g => {
        if (selectedLanguages.has(g.language)) {
          newGenres.add(g.key);
        }
      });
      availableSubgenres.forEach(s => {
        if (selectedLanguages.has(s.language)) {
          newSubgenres.add(s.key);
        }
      });
      setSelectedGenres(newGenres);
      setSelectedSubgenres(newSubgenres);
    } else if (step === 3) {
      // Select all subgenres for selected genres
      const newSubgenres = new Set();
      availableSubgenres.forEach(s => {
        if (selectedGenres.has(`${s.language}|${s.genre}`)) {
          newSubgenres.add(s.key);
        }
      });
      setSelectedSubgenres(newSubgenres);
    }
  };

  const deselectAll = () => {
    if (step === 1) {
      setSelectedLanguages(new Set());
      setSelectedGenres(new Set());
      setSelectedSubgenres(new Set());
    } else if (step === 2) {
      setSelectedGenres(new Set());
      setSelectedSubgenres(new Set());
    } else if (step === 3) {
      setSelectedSubgenres(new Set());
    }
  };

  // Filter genres/subgenres based on selected languages/genres
  const filteredGenres = availableGenres.filter(g => selectedLanguages.has(g.language));
  const filteredSubgenres = availableSubgenres.filter(s => 
    selectedLanguages.has(s.language) && selectedGenres.has(`${s.language}|${s.genre}`)
  );

  // Check if hierarchy is loading
  const isHierarchyLoading = !hierarchy || availableLanguages.length === 0;

  return (
    <div className="setup-wizard-overlay">
      <div className="setup-wizard">
        {/* Close Button */}
        {onClose && (
          <button 
            className="wizard-close-btn" 
            onClick={onClose}
            title="Close (you can return anytime)"
          >
            ✕
          </button>
        )}
        
        {/* Progress Bar */}
        <div className="wizard-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
          <div className="progress-steps">
            <span className={step >= 0 ? 'active' : ''}>Welcome</span>
            <span className={step >= 1 ? 'active' : ''}>Languages</span>
            <span className={step >= 2 ? 'active' : ''}>Genres</span>
            <span className={step >= 3 ? 'active' : ''}>Subgenres</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="wizard-content">
          {step === 0 && (
            <div className="wizard-step welcome-step">
              <h1>🎵 Welcome to Jookbox!</h1>
              <p>Let's set up your personal music collection.</p>
              <p>You can choose which languages, genres, and subgenres you want to include in your library.</p>
              <p className="wizard-note">
                <strong>Everything is selected by default.</strong> Just uncheck what you don't want.
              </p>
              <div className="wizard-actions">
                <button className="wizard-btn secondary" onClick={handleSkip} disabled={loading}>
                  Skip Setup (Keep Everything)
                </button>
                <button className="wizard-btn primary" onClick={() => setStep(1)}>
                  Customize My Collection →
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="wizard-step">
              <h2>Select Languages</h2>
              <p>Choose which languages you want in your collection:</p>
              
              {isHierarchyLoading ? (
                <div className="wizard-loading">
                  <div className="loading-spinner">⏳</div>
                  <p>Loading your music library...</p>
                  <p className="loading-hint">Please wait while we fetch your collection data.</p>
                </div>
              ) : (
                <>
                  <div className="wizard-bulk-actions">
                    <button onClick={selectAll}>Select All</button>
                    <button onClick={deselectAll}>Deselect All</button>
                  </div>
                  <div className="wizard-options">
                    {availableLanguages.map(language => (
                      <label key={language} className="wizard-option">
                        <input
                          type="checkbox"
                          checked={selectedLanguages.has(language)}
                          onChange={() => toggleLanguage(language)}
                        />
                        <span className="option-label">{language}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              
              <div className="wizard-actions">
                <button className="wizard-btn secondary" onClick={() => setStep(0)}>
                  ← Back
                </button>
                <button className="wizard-btn secondary" onClick={handleSkip} disabled={loading}>
                  Skip
                </button>
                <button 
                  className="wizard-btn primary" 
                  onClick={() => setStep(2)}
                  disabled={isHierarchyLoading || selectedLanguages.size === 0}
                >
                  Next: Genres →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="wizard-step">
              <h2>Select Genres</h2>
              <p>Choose which genres you want for your selected languages:</p>
              <div className="wizard-bulk-actions">
                <button onClick={selectAll}>Select All</button>
                <button onClick={deselectAll}>Deselect All</button>
              </div>
              <div className="wizard-options grouped">
                {Array.from(selectedLanguages).sort().map(language => {
                  const languageGenres = filteredGenres.filter(g => g.language === language);
                  if (languageGenres.length === 0) return null;
                  return (
                    <div key={language} className="option-group">
                      <h3>{language}</h3>
                      <div className="option-group-items">
                        {languageGenres.map(g => (
                          <label key={g.key} className="wizard-option">
                            <input
                              type="checkbox"
                              checked={selectedGenres.has(g.key)}
                              onChange={() => toggleGenre(g.key)}
                            />
                            <span className="option-label">{g.genre}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="wizard-actions">
                <button className="wizard-btn secondary" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button className="wizard-btn secondary" onClick={handleSkip} disabled={loading}>
                  Skip
                </button>
                <button 
                  className="wizard-btn primary" 
                  onClick={() => setStep(3)}
                  disabled={selectedGenres.size === 0}
                >
                  Next: Subgenres →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="wizard-step">
              <h2>Select Subgenres</h2>
              <p>Fine-tune your collection by selecting specific subgenres:</p>
              <div className="wizard-bulk-actions">
                <button onClick={selectAll}>Select All</button>
                <button onClick={deselectAll}>Deselect All</button>
              </div>
              <div className="wizard-options grouped">
                {Array.from(selectedGenres).sort().map(genreKey => {
                  const [language, genre] = genreKey.split('|');
                  const genreSubgenres = filteredSubgenres.filter(
                    s => s.language === language && s.genre === genre
                  );
                  if (genreSubgenres.length === 0) return null;
                  return (
                    <div key={genreKey} className="option-group">
                      <h3>{language} → {genre}</h3>
                      <div className="option-group-items">
                        {genreSubgenres.map(s => (
                          <label key={s.key} className="wizard-option">
                            <input
                              type="checkbox"
                              checked={selectedSubgenres.has(s.key)}
                              onChange={() => toggleSubgenre(s.key)}
                            />
                            <span className="option-label">{s.subgenre}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="wizard-actions">
                <button className="wizard-btn secondary" onClick={() => setStep(2)}>
                  ← Back
                </button>
                <button 
                  className="wizard-btn primary" 
                  onClick={handleComplete}
                  disabled={loading || selectedSubgenres.size === 0}
                >
                  {loading ? 'Saving...' : 'Complete Setup ✓'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SetupWizard;

